import React, { useState, useRef, useEffect } from 'react';
import { Upload, Box, Trash2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CrownOutlined } from '@ant-design/icons';
import { App } from 'antd';
import { useEditorStore } from '../store/useEditorStore';
import { useAuthStore, getFeatureFromQuota } from '../stores/auth';
import LoadingOverlay from './LoadingOverlay';
import { useTranslation } from 'react-i18next';

interface FurnitureModel {
  id: string;
  name: string;
  thumbnail?: string;
  modelUrl: string;
  isBuiltIn: boolean;
  category: string;
  width?: number;  // models.json: 宽度（厘米）
  height?: number; // models.json: 高度（厘米）
  depth?: number;  // models.json: 深度（厘米）
}

interface Category {
  id: string;
  name: string;
  models: {
    id: string;
    name: string;
    file?: string;
    path?: string;  // 兼容旧格式
    thumbnail?: string;
    width?: number;
    height?: number;
    depth?: number;
  }[];
}

interface ModelsConfig {
  categories: Category[];
}

interface FurnitureLibraryProps {
  onClose: () => void;
}

const FurnitureLibrary: React.FC<FurnitureLibraryProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [builtInModels, setBuiltInModels] = useState<FurnitureModel[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [customModels, setCustomModels] = useState<FurnitureModel[]>(() => {
    const saved = localStorage.getItem('furniture-library-custom');
    return saved ? JSON.parse(saved) : [];
  });
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  
  const { startModel3DPlacement, project } = useEditorStore();
  const { quota } = useAuthStore();
  const hasModels = getFeatureFromQuota(quota?.permissions, 'models');

  // 从 models.json 加载内置模型
  useEffect(() => {
    loadModelsConfig();
  }, []);

  const loadModelsConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/KiMap_3D_Model/models.json');
      if (!response.ok) throw new Error('Failed to load models config');
      
      const config: ModelsConfig = await response.json();
      
      // 提取分类
      const cats = config.categories.map(c => ({ id: c.id, name: c.name }));
      setCategories(cats);
      
      // 提取所有内置模型
      const models: FurnitureModel[] = [];
      config.categories.forEach(category => {
        category.models.forEach(model => {
          const modelFile = model.file || model.path || '';
          models.push({
            id: `builtin-${category.id}-${model.id}`,
            name: model.name,
            modelUrl: `/KiMap_3D_Model/${category.id}/${modelFile}`,
            thumbnail: model.thumbnail ? `/KiMap_3D_Model/${category.id}/${model.thumbnail}` : undefined,
            isBuiltIn: true,
            category: category.id,
            width: model.width,   // cm
            height: model.height, // cm
            depth: model.depth,   // cm
          });
        });
      });
      
      setBuiltInModels(models);
    } catch (error) {
      console.error('Failed to load models config:', error);
      message.error(t('furniture.loadFailed'));
    } finally {
      setLoading(false);
    }
  };


  // 保存自定义模型到 localStorage
  const saveCustomModels = (models: FurnitureModel[]) => {
    localStorage.setItem('furniture-library-custom', JSON.stringify(models));
    setCustomModels(models);
  };

  // 导入3D模型
  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['.obj', '.glb', '.gltf', '.fbx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validTypes.includes(ext)) {
      message.warning('请上传 OBJ、GLB、GLTF 或 FBX 格式的3D模型文件');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      message.error('模型文件大小不能超过 50MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = () => {
        const modelUrl = reader.result as string;
        const newModel: FurnitureModel = {
          id: `custom-${Date.now()}`,
          name: file.name.replace(ext, ''),
          modelUrl,
          isBuiltIn: false,
          category: 'other',
        };
        
        const updatedModels = [...customModels, newModel];
        saveCustomModels(updatedModels);
        message.success(t('furniture.importSuccess', { name: newModel.name }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to import model:', error);
      message.error(t('furniture.importFailed'));
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = (modelId: string) => {
    const updatedModels = customModels.filter(m => m.id !== modelId);
    saveCustomModels(updatedModels);
    message.success('模型已删除');
  };

  const handleSelectModel = (model: FurnitureModel) => {
    // 预加载缩略图
    let previewImage: HTMLImageElement | undefined;
    if (model.thumbnail) {
      previewImage = new Image();
      previewImage.src = model.thumbnail;
    }
    
    // 从 models.json 获取模型配置的尺寸（单位：厘米），转换为毫米
    // 注意：models.json 的字段语义为 width/height/depth（cm），不要把 height 当 depth
    const realModelWidth = (model.width ?? 50) * 10;   // cm → mm (默认50cm = 500mm)
    const realModelDepth = (model.depth ?? 50) * 10;   // cm → mm
    const realModelHeight = (model.height ?? 75) * 10; // cm → mm（默认75cm）
    
    // 获取校准信息
    const isCalibrated = project?.mapConfig?.isCalibrated || false;
    const isAutoCalibrated = project?.mapConfig?.autoCalibrated || false;
    const calibrationScale = project?.mapConfig?.calibrationScale || 1; // px/cm
    const baseMap = project?.mapConfig?.baseMap;
    
    // 计算2D画布上的像素尺寸
    // 自动校准：世界坐标直接是厘米，1世界单位=1厘米
    // 手动校准：需要用 calibrationScale 转换
    let canvasWidth: number;
    let canvasDepth: number;
    
    if (isAutoCalibrated && baseMap) {
      // 自动校准：canvas尺寸直接等于厘米值（因为世界坐标就是厘米）
      canvasWidth = realModelWidth / 10;   // mm → cm（直接作为世界坐标）
      canvasDepth = realModelDepth / 10;   // mm → cm
    } else if (isCalibrated && calibrationScale && baseMap) {
      // 手动校准：真实尺寸(mm) → cm → 像素（使用校准比例）
      // calibrationScale = 底图像素 / 真实厘米
      canvasWidth = (realModelWidth / 10) * calibrationScale;
      canvasDepth = (realModelDepth / 10) * calibrationScale;
    } else {
      // 未校准：使用默认比例（1cm = 10px）
      canvasWidth = (realModelWidth / 10) * 10;
      canvasDepth = (realModelDepth / 10) * 10;
    }
    
    console.log('🪑 选择模型:', {
      name: model.name,
      category: model.category,
      realSize: { width: realModelWidth, depth: realModelDepth, height: realModelHeight, unit: 'mm' },
      realSizeCm: { width: realModelWidth/10, depth: realModelDepth/10, unit: 'cm' },
      calibrationScale: calibrationScale.toFixed(4) + ' px/cm',
      isAutoCalibrated,
      canvasSize: { width: canvasWidth.toFixed(2), depth: canvasDepth.toFixed(2), unit: 'px' },
      threeDSize: { width: (canvasWidth/100).toFixed(2), depth: (canvasDepth/100).toFixed(2), unit: 'm' },
      isCalibrated,
      hasConfiguredDimensions: !!(model.width && model.depth)
    });
    
    // 如果没有校准数据，显示警告
    if (!isCalibrated) {
      console.warn('⚠️ 地图未校准，使用默认比例 (10px/cm)。建议先使用校准工具测量底图。');
    }
    
    // 如果模型没有配置尺寸，显示警告
    if (!model.width || !model.depth) {
      console.warn(`⚠️ 模型 "${model.name}" 未配置尺寸，使用默认值 500mm × 500mm`);
    }
    
    // 进入3D模型放置模式
    startModel3DPlacement({
      modelPath: model.modelUrl,
      modelName: model.name,
      modelCategory: model.category, // 传递分类信息
      width: canvasWidth,
      depth: canvasDepth,
      height: realModelHeight,
      realWidth: realModelWidth,
      realDepth: realModelDepth,
      isBuiltIn: model.isBuiltIn,
      thumbnail: model.thumbnail,
      previewImage,
    });
    message.success(t('furniture.selected', { name: model.name }));
    onClose();
  };

  // 合并所有模型
  const allModels = [...builtInModels, ...customModels];
  
  // 按分类筛选
  const filteredModels = activeCategory === 'all' 
    ? allModels 
    : allModels.filter(m => m.category === activeCategory);

  // 获取所有分类（包含自定义模型的分类）
  const allCategories = [
    { id: 'all', name: t('furniture.all') },
    ...categories,
  ];

  const navigate = useNavigate();
  const MODELS_PLANS = [
    { name: 'Personal', packageLevel: 2 },
    { name: 'Professional', packageLevel: 3 },
    { name: 'Enterprise', packageLevel: 4 },
  ];

  const PLAN_ORDER = ['Free', 'Personal', 'Professional', 'Enterprise'];
  const PLAN_COLORS: Record<string, string> = {
    Free: 'bg-gray-100 text-gray-600',
    Personal: 'bg-blue-50 text-blue-600',
    Professional: 'bg-purple-50 text-purple-600',
    Enterprise: 'bg-amber-50 text-amber-600',
  };

  const sortedPlans = [...MODELS_PLANS].sort(
    (a, b) => PLAN_ORDER.indexOf(a.name) - PLAN_ORDER.indexOf(b.name)
  );

  const handleUpgrade = () => navigate('/subscription');

  const panel = (
    <div
      style={{ height: 480 }}
    className="absolute left-full top-0 ml-1 bg-white rounded-lg shadow-lg border border-gray-200 z-[1000] w-72">
      {/* 标题栏 */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Box size={16} className="text-primary-500" />
          <span className="font-medium text-gray-800">{t('furniture.title')}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={loadModelsConfig}
            className="p-1.5 text-gray-500 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors"
            title={t('furniture.refresh')}
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={handleImport}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors"
          >
            <Upload size={12} />
            {t('furniture.import')}
          </button>
        </div>
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".obj,.glb,.gltf,.fbx"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* 分类标签 */}
      <div className="px-3 py-2 border-b border-gray-100 flex gap-1 flex-wrap">
        {allCategories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${
              activeCategory === cat.id
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* 模型列表 */}
      <div className="max-h-80 overflow-y-auto p-2">
        {loading ? (
          <LoadingOverlay text={t('furniture.loading')} />
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            <Box size={32} className="mx-auto mb-2 opacity-50" />
            <p>{t('furniture.noModels')}</p>
            <p className="text-xs mt-1">{t('furniture.noModelsHint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredModels.map(model => (
              <div
                key={model.id}
                onClick={() => handleSelectModel(model)}
                className="relative group bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-primary-50 hover:border-primary-200 border border-transparent transition-all"
              >
                {/* 模型缩略图 */}
                <div className="w-full h-16 bg-gray-200 rounded flex items-center justify-center mb-2 overflow-hidden">
                  {model.thumbnail ? (
                    <img src={model.thumbnail} alt={model.name} className="w-full h-full object-cover" />
                  ) : (
                    <Box size={24} className="text-gray-400" />
                  )}
                </div>
                
                {/* 模型名称 */}
                <div className="text-xs text-gray-700 truncate text-center" title={model.name}>
                  {model.name}
                </div>
                
                {/* 内置标签 */}
                {model.isBuiltIn && (
                  <div className="absolute top-1 left-1 px-1 py-0.5 text-[10px] bg-primary-100 text-primary-600 rounded">
                    {t('furniture.builtin')}
                  </div>
                )}
                
                {/* 删除按钮（仅自定义模型） */}
                {!model.isBuiltIn && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(model.id);
                    }}
                    className="absolute top-1 right-1 p-1 bg-red-100 text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-200"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部提示 */}
      <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-400 text-center">
        {t('furniture.formatHint')}
      </div>
    </div>
  );

  if (!hasModels) {
    return (
      <div v-if="!hasModels">
        {panel}
        <div
          style={{ height: 480 }}
          className="absolute left-full top-0 ml-1 w-72 rounded-lg z-[1001] bg-white/85 backdrop-blur-[1px] flex flex-col items-center justify-center gap-4 p-6"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        >
          <CrownOutlined className="text-amber-500 text-3xl" />
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-800 mb-1">
              {t('permissionCard.modelsLibrary')}
            </div>
            <div className="text-xs text-gray-500 mb-3">
              {t('permissionCard.unlockFeature')}
            </div>
          </div>
          <div className="text-xs text-gray-500 mb-1">{t('permissionCard.availableInPlans')}</div>
          <div className="flex flex-wrap justify-center gap-1 mb-4">
            {sortedPlans.map((plan) => (
              <span
                key={plan.name}
                className={`px-2 py-0.5 rounded text-xs font-medium ${PLAN_COLORS[plan.name]}`}
              >
                {plan.name}
              </span>
            ))}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleUpgrade(); }}
            className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t('permissionCard.upgradeNow')}
          </button>
        </div>
      </div>
    );
  }

  return panel;
};

export default FurnitureLibrary;
